import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  BellRing,
  CheckCircle2,
  FilePen,
  Mail,
  Megaphone,
  Plus,
  Send,
  Trash2,
  UserSearch,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAdminBroadcasts,
  useAdminBroadcastStats,
  useCreateBroadcast,
  useSendBroadcast,
  useResendBroadcast,
  useDeleteBroadcast,
  type AdminBroadcast,
} from "@/hooks/useAdminBroadcasts";

export const Route = createFileRoute("/admin/notifications/")({
  component: AdminNotificationsMgmtPage,
});

/** Delete icon that opens a one-item confirmation menu (the row menu). */
function RowDeleteMenu({
  open,
  onOpen,
  onClose,
  onDelete,
}: {
  open: boolean;
  onOpen: (anchorEl: HTMLElement) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next && triggerRef.current) onOpen(triggerRef.current);
        else if (!next) onClose();
      }}
    >
      <DropdownMenuTrigger asChild>
        <IconAction ref={triggerRef} label="Delete" tone="destructive" icon={<Trash2 />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={onDelete}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <Trash2 />
          Delete broadcast
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Audience = "all" | "students" | "partners";

const AUDIENCE_LABEL: Record<Audience, string> = {
  all: "All students & partners",
  students: "Students only",
  partners: "Partner organizations only",
};

const formatTimestamp = (broadcast: AdminBroadcast): string => {
  const raw = broadcast.sent_at ?? broadcast.created_at;
  if (!raw) return broadcast.date ?? "—";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return broadcast.date ?? "—";
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function AdminNotificationsMgmtPage() {
  const [search, setSearch] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [form, setForm] = useState({
    title: "",
    message: "",
    type: "system",
    target_audience: "all" as Audience,
  });
  const [rowMenu, setRowMenu] = useState<{ anchorEl: HTMLElement; broadcast: AdminBroadcast } | null>(
    null,
  );

  const { data: broadcasts = [], isLoading } = useAdminBroadcasts();
  const { data: stats } = useAdminBroadcastStats();

  const createBroadcast = useCreateBroadcast();
  const sendBroadcast = useSendBroadcast();
  const resendBroadcast = useResendBroadcast();
  const deleteBroadcast = useDeleteBroadcast();

  const filteredBroadcasts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return broadcasts;
    return broadcasts.filter(
      (b) =>
        b.title.toLowerCase().includes(term) || b.message.toLowerCase().includes(term),
    );
  }, [broadcasts, search]);

  const closeCreateDialog = () => {
    setCreateDialog(false);
    setForm({ title: "", message: "", type: "system", target_audience: "all" });
  };

  const handleCreate = (sendNow: boolean) => {
    createBroadcast.mutate(
      {
        title: form.title.trim(),
        message: form.message.trim(),
        type: form.type,
        target_audience: form.target_audience,
        send_now: sendNow,
      },
      { onSuccess: closeCreateDialog },
    );
  };

  const handleDelete = (broadcast: AdminBroadcast) => {
    deleteBroadcast.mutate(broadcast.id);
  };

  const deliveryRate = stats ? `${stats.delivery_rate.toFixed(1)}%` : "—";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Communications hub"
        subtitle="Manage broadcast announcements, targeted alerts, and system-wide messaging workflows."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Notifications" }]}
        action={
          <Button onClick={() => setCreateDialog(true)}>
            <Plus />
            New broadcast
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total alerts sent" value={stats?.total_sent ?? 0} icon={<CheckCircle2 />} />
        <StatCard label="Message drafts" value={stats?.drafts ?? 0} icon={<Mail />} />
        <StatCard label="Average delivery rate" value={deliveryRate} icon={<BellRing />} />
      </div>

      <SectionCard
        title="Broadcast history"
        noPadding
        actions={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search history…"
            aria-label="Search broadcast history"
          />
        }
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={5} />
          </div>
        ) : filteredBroadcasts.length === 0 ? (
          <EmptyState
            bare
            icon={<Megaphone />}
            title={
              broadcasts.length === 0
                ? "No notification history found."
                : "No broadcasts match your search."
            }
            description={
              broadcasts.length === 0
                ? "Broadcasts you send or save as drafts will appear here."
                : "Try a different search term."
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Notification details</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Target audience</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBroadcasts.map((broadcast) => (
                <TableRow key={broadcast.id}>
                  <TableCell className="min-w-64 pl-4 md:pl-5">
                    <p className="font-medium text-foreground">{broadcast.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">
                      {broadcast.message}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {broadcast.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-body">
                      <UserSearch className="size-3.5 text-muted-foreground" aria-hidden />
                      {broadcast.recipients || AUDIENCE_LABEL[broadcast.target_audience]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={broadcast.status === "sent" ? "success" : "warning"} dot className="capitalize">
                      {broadcast.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatTimestamp(broadcast)}
                  </TableCell>
                  <TableCell className="pr-4 text-right md:pr-5">
                    <div className="flex justify-end gap-0.5">
                      {broadcast.status === "sent" ? (
                        <IconAction
                          label="Resend to current audience"
                          icon={<Send />}
                          disabled={resendBroadcast.isPending}
                          onClick={() => resendBroadcast.mutate(broadcast.id)}
                        />
                      ) : (
                        <IconAction
                          label="Dispatch now"
                          icon={<Send />}
                          className="text-foreground"
                          disabled={sendBroadcast.isPending}
                          onClick={() => sendBroadcast.mutate(broadcast.id)}
                        />
                      )}
                      <RowDeleteMenu
                        open={rowMenu?.broadcast.id === broadcast.id}
                        onOpen={(anchorEl) => setRowMenu({ anchorEl, broadcast })}
                        onClose={() => setRowMenu(null)}
                        onDelete={() => {
                          if (rowMenu) handleDelete(rowMenu.broadcast);
                          setRowMenu(null);
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={createDialog} onOpenChange={(o) => !o && closeCreateDialog()}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="size-4 text-muted-foreground" aria-hidden />
              New broadcast
            </DialogTitle>
            <DialogDescription>
              Draft a message to be sent via platform notifications and linked email accounts.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <Field label="Subject / title" required>
              <Input
                placeholder="e.g. System maintenance update"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </Field>
            <Field label="Message content" required>
              <Textarea
                rows={4}
                placeholder="Write your announcement details here..."
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </Field>
            <Field label="Category">
              {({ id }) => (
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="info">Information</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="placement">Placement</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Target audience" hint="Admin accounts are never included in broadcasts.">
              {({ id, describedBy }) => (
                <Select
                  value={form.target_audience}
                  onValueChange={(v) => setForm((f) => ({ ...f, target_audience: v as Audience }))}
                >
                  <SelectTrigger id={id} aria-describedby={describedBy}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{AUDIENCE_LABEL.all}</SelectItem>
                    <SelectItem value="students">{AUDIENCE_LABEL.students}</SelectItem>
                    <SelectItem value="partners">{AUDIENCE_LABEL.partners}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleCreate(false)}
              disabled={createBroadcast.isPending || !form.title.trim() || !form.message.trim()}
            >
              <FilePen />
              Save as draft
            </Button>
            <Button
              onClick={() => handleCreate(true)}
              disabled={createBroadcast.isPending || !form.title.trim() || !form.message.trim()}
            >
              <Send />
              Dispatch now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
